
import { useCallback, useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { parseError, type ParsedError } from '@/lib/errorParser';

interface ErrorReport {
  id: string;
  created_at: string;
  reporter_id: string | null;
  path: string;
  message: string;
  stack: string | null;
  context: any;
  status: string;
  assigned_to: string | null;
  resolved_at: string | null;
  suggested_fix: string | null;
  ai_model: string | null;
  last_ai_run_at: string | null;
  error_code?: string;
  error_details?: any;
  http_status?: number;
  supabase_hint?: string;
  function_name?: string;
  request_body?: any;
  response_body?: any;
}

export const useErrorReporting = () => {
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState<ErrorReport[]>([]);
  const { toast } = useToast();
  const { user } = useAuth();

  // Stable identity: callers put reportError in effect deps (useSharePointAuth
  // looped on it, 28-09-2026).
  const reportError = useCallback(async (
    message: string,
    error?: Error,
    additionalContext?: any
  ) => {
    try {
      if (!user) {
        console.warn('[ERROR_REPORTING] No authenticated user, skipping error report');
        return false;
      }

      setLoading(true);

      // Parse error for all details
      const parsedError: ParsedError | null = error ? parseError(error, additionalContext) : null;

      console.log('[ERROR_REPORTING] Reporting detailed error:', { 
        message, 
        parsedError, 
        context: additionalContext 
      });

      const errorData = {
        path: window.location.pathname,
        message: message,
        stack: parsedError?.stack || null,
        error_code: parsedError?.code || null,
        error_details: parsedError?.details || null,
        http_status: parsedError?.httpStatus || null,
        supabase_hint: parsedError?.hint || null,
        function_name: parsedError?.functionName || null,
        request_body: parsedError?.requestBody || additionalContext?.requestBody || null,
        response_body: parsedError?.responseBody || null,
        context: {
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          url: window.location.href,
          userId: user.id,
          ...additionalContext,
          // Include parsed error summary for AI analysis
          parsedErrorSummary: parsedError ? {
            message: parsedError.message,
            code: parsedError.code,
            httpStatus: parsedError.httpStatus,
            functionName: parsedError.functionName
          } : null
        }
      };

      console.log('[ERROR_REPORTING] Full error data:', errorData);

      const { data, error: supabaseError } = await supabase
        .from('error_reports')
        .insert(errorData)
        .select()
        .single();

      if (supabaseError) {
        console.error('[ERROR_REPORTING] Failed to report error:', supabaseError);
        return false;
      }

      console.log('[ERROR_REPORTING] Error reported successfully:', data);
      
      toast({
        title: 'Foutmelding verzonden',
        description: 'Je foutmelding is verzonden naar de systeembeheerder.',
      });

      return true;
    } catch (err) {
      console.error('[ERROR_REPORTING] Error in reportError:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [user, toast]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      console.log('[ERROR_REPORTING] Fetching error reports...');

      const { data, error } = await supabase
        .from('error_reports')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[ERROR_REPORTING] Error fetching reports:', error);
        toast({
          title: 'Fout bij laden',
          description: 'Kon foutmeldingen niet laden.',
          variant: 'destructive'
        });
        return;
      }

      console.log('[ERROR_REPORTING] Fetched reports:', data?.length || 0);
      setReports(data || []);
    } catch (err) {
      console.error('[ERROR_REPORTING] Error in fetchReports:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateAISolution = async (reportId: string, errorMessage: string, context: any) => {
    try {
      console.log('[ERROR_REPORTING] Generating AI solution for report:', reportId);

      // Find the full report to get all details
      const report = reports.find(r => r.id === reportId);

      const { data, error } = await supabase.functions.invoke('generate-error-solution', {
        body: {
          reportId,
          errorMessage,
          context,
          path: context.url || report?.path || 'unknown',
          errorCode: report?.error_code,
          httpStatus: report?.http_status,
          functionName: report?.function_name,
          requestBody: report?.request_body,
          responseBody: report?.response_body,
          supabaseHint: report?.supabase_hint,
          errorDetails: report?.error_details
        }
      });

      if (error) {
        console.error('[ERROR_REPORTING] Error generating AI solution:', error);
        
        // Parse the error message to provide better feedback
        let errorTitle = 'AI fout';
        let errorDescription = 'Kon geen AI-oplossing genereren.';
        
        if (error.message?.includes('OpenAI API key not configured')) {
          errorTitle = 'OpenAI API-sleutel ontbreekt';
          errorDescription = 'Voeg je OpenAI API-sleutel toe onder Project Settings > Secrets om AI-oplossingen te genereren.';
        } else if (error.message?.includes('insufficient_quota') || error.message?.includes('quota')) {
          errorTitle = 'OpenAI quota bereikt';
          errorDescription = 'Je OpenAI API-account heeft onvoldoende credits. Voeg credits toe aan je OpenAI-account.';
        } else if (error.message?.includes('rate_limit')) {
          errorTitle = 'OpenAI rate limit';
          errorDescription = 'Te veel verzoeken naar OpenAI. Probeer het over een paar minuten opnieuw.';
        } else if (error.message?.includes('invalid_api_key')) {
          errorTitle = 'Ongeldige OpenAI API-sleutel';
          errorDescription = 'Controleer je OpenAI API-sleutel in Project Settings > Secrets.';
        } else if (error.message?.includes('model_not_found')) {
          errorTitle = 'OpenAI model niet gevonden';
          errorDescription = 'Het opgegeven AI-model is niet beschikbaar. Controleer de configuratie.';
        }
        
        toast({
          title: errorTitle,
          description: errorDescription,
          variant: 'destructive'
        });
        return false;
      }

      console.log('[ERROR_REPORTING] AI solution generated:', data);
      await fetchReports(); // Refresh data
      
      toast({
        title: 'AI-oplossing gegenereerd',
        description: 'Een AI-oplossing is succesvol gegenereerd voor deze foutmelding.',
      });
      
      return true;
    } catch (err) {
      console.error('[ERROR_REPORTING] Error in generateAISolution:', err);
      toast({
        title: 'Onbekende fout',
        description: 'Er ging iets mis bij het genereren van de AI-oplossing.',
        variant: 'destructive'
      });
      return false;
    }
  };

  const updateReportStatus = async (reportId: string, status: string, assignedTo?: string) => {
    try {
      const updates: any = { status };
      if (assignedTo) updates.assigned_to = assignedTo;
      if (status === 'resolved') updates.resolved_at = new Date().toISOString();

      const { error } = await supabase
        .from('error_reports')
        .update(updates)
        .eq('id', reportId);

      if (error) throw error;

      toast({
        title: 'Status bijgewerkt',
        description: 'De status van de foutmelding is bijgewerkt.',
      });

      await fetchReports();
      return true;
    } catch (error) {
      console.error('[ERROR_REPORTING] Error updating status:', error);
      toast({
        title: 'Fout bij bijwerken',
        description: 'Kon status niet bijwerken.',
        variant: 'destructive'
      });
      return false;
    }
  };

  return {
    loading,
    reports,
    reportError,
    fetchReports,
    generateAISolution,
    updateReportStatus
  };
};
